import React, { useState, useLayoutEffect, useEffect, useContext, lazy, Suspense } from "react";
import Input from "components/input/input";
import toast from "react-hot-toast";
import Button from "components/button/button";
import LoadingIndicator from "assets/svg/loading-indicator";
import ClockIconSVG from "assets/svg/clock-icon";
import CheckCircleIconSVG from "assets/svg/check-circle-icon";
import CheckCircle2IconSVG from "assets/svg/check-circle2-icon";
import PaypalIconSVG from "assets/svg/paypal-icon-svg";
import CardIconSVG from "assets/svg/card-icon-svg";
import Layout from "layouts/layout";
import { Link, useHistory, useLocation } from "react-router-dom";
import userActions from "redux/modules/user/actions";
import { connect } from "react-redux";
import PasswordValidation from "components/password-validation/password-validation";
import { generateNDigitID, randomExtension } from "utils/utils";
import { gsap } from "gsap";
import CustomCheckbox from "components/checkbox/custom-checkbox";
import { RECAPTCHA_KEY } from "configs/api";
import ReCAPTCHA from "react-google-recaptcha";
import _ from "lodash";
import { SubscriptionContext } from "context/subscription";
import { useTranslation } from "react-i18next";
import { NavLink } from "react-router-dom/cjs/react-router-dom";
import { BASE_URL } from "configs/api";
import { ENV } from "configs/env";
import { STRIPE_AUD_PREMIUM_LIFETIME, PAYPAL_AUD_PREMIUM_LIFETIME } from "configs/subscription";
import { FeaturedIcon, LogoBlueIcon, AvatarHeader } from "constants/images";
import { SampleBG } from "constants/images";
import LogoAlt from "assets/images/logo-colored.png";
import { FaStar } from "react-icons/fa";
import ReactGA from "react-ga4";


const existingUsernameCache = new Set();

// function useQuery() {
//   return new URLSearchParams(useLocation().search);
// }

const SignUp = ({ user, setUserData }) => {
  const location = useLocation();
  const { t } = useTranslation();
  const { http, localstorage } = global.services;
  const history = useHistory();
  const query = useQuery();
  const status = query.get("status");
  const { getSubscription } = useContext(SubscriptionContext);
  const [recaptcha, setRecaptcha] = useState(null);
  const [recapthcaError, setRecapthcaError] = useState(null);
  const [state, setState] = useState({
    first_name: "",
    last_name: "",
    email: "",
    username: "",
    password: "",
    password_confirmation: "",
    terms: false,
    loading: false,
    showPassword: false,
    showPasswordConfirm: false,
    validationErrors: null,
    success: false,
    role: "customer",
    recaptchaToken: "",
    over_18_years_old: false,
    selectedPlan: "lifetime",
    showFreePlan: false
  });
  const [passwordValidation, setPasswordValidation] = useState({
    passwordHasUpperCaseLetter: false,
    passwordHasLowerCaseLetter: false,
    passwordHasNumber: false,
    passwordHasEightCharacters: false,
  });

  const FEATURED_KEY = "featured_profiles_list";
  const FEATURED_IDX_KEY = "featured_profiles_idx";

  const pickAndRotate = (list) => {
    if (!Array.isArray(list) || list.length === 0) return null;
    let idx = 0;
    try {
      const res = localstorage.get(FEATURED_IDX_KEY);
      if (res?.success && typeof res.value === "string") {
        const parsed = parseInt(res.value, 10);
        if (!Number.isNaN(parsed)) idx = parsed;
      }
    } catch (_) {}
    const current = list[idx % list.length];
    const nextIdx = (idx + 1) % list.length;
    localstorage.set(FEATURED_IDX_KEY, String(nextIdx));
    return current;
  };

  /**
   * @typedef {"sign-up" | "verify" | "choose-plan"} View
   * @type {[view: View, setView: (value: View) => void]}
   */
  const [view, setView] = useState("sign-up");
  const email = query.get("verify");

  useEffect(() => {
    const hasSent = localStorage.getItem("verify_sent") === "1";
    const verify = query.get("verify");
    const showChoosePlan = query.get("chooseplan") === "1";
    const showFreePlan = query.get("account") !== "lifetime";
    const signupEmailResp = localstorage.get("signup_email");
    const signup_email = signupEmailResp?.success ? signupEmailResp.value : "";
    setState((prev) => ({ ...prev, showFreePlan, email: signup_email }));
    if(showChoosePlan && !signup_email){
      window.location.href = `${BASE_URL}/sign-up`;
    }else if(verify && !hasSent){
      handleResendEmailVerification();
      setView("verify");
      localStorage.setItem("verify_sent", "1"); // mark as sent permanently
    }
    else {
      setView(verify ? "verify" : showChoosePlan && signup_email ? "choose-plan" : "sign-up");
    }
    
  }, [query]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "password") {
      const hasUpperCase = /[A-Z]/.test(value);
      const hasLowerCase = /[a-z]/.test(value);
      const hasEightCharacters = value.length >= 8;
      const hasNumber = /\d/.test(value);

      setPasswordValidation((prevState) => ({
        ...prevState,
        passwordHasUpperCaseLetter: hasUpperCase,
        passwordHasLowerCaseLetter: hasLowerCase,
        passwordHasEightCharacters: hasEightCharacters,
        passwordHasNumber: hasNumber,
      }));
    }
    setState((prev) => ({
      ...prev,
      [name]: value,
      validationErrors: { ...state.validationErrors, [name]: null },
    }));
  };

  const handleUpdateCheckbox = (e) => {
    const isChecked = e.target.checked;
    setState({ ...state, terms: isChecked });
  };

  const handleUpdateCheckboxOver18Years = (e) => {
    const isChecked = e.target.checked;
    setState({ ...state, over_18_years_old: isChecked });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (RECAPTCHA_KEY) {
      if (!recaptcha) {
        return setRecapthcaError("Recaptcha is required.");
      }

      state.recaptchaToken = recaptcha;
    }

    setState((prev) => ({ ...prev, loading: true }));

    let payload = { ...state };

    if (status) {
      payload.status = status;
    }
    const userLanguage = navigator.language || navigator.userLanguage;
    const urlParams = new URLSearchParams(window.location.search);
    let limit = urlParams.get("limit");
    if (!_.isEmpty(limit)) {
      payload = { ...payload, plan: urlParams.get("limit"), lang:userLanguage };
    }
    try {
  await http.post("auth/register", payload);
  ReactGA.event({
    category: "Registration",
    action: "Sign-up",// helps track per-user in GA4
  });

  // After account creation, switch to inline choose-plan view (no auto login)
  const searchParams = new URLSearchParams(location.search);
  localstorage.set("signup_email", state.email);
  searchParams.delete("verify");
  // searchParams.delete("account");
  searchParams.set("chooseplan", "1");
  history.replace({ pathname: location.pathname, search: searchParams.toString() });
    } catch (error) {
      if (error.status === 422) {
        return setState((prev) => ({
          ...prev,
          validationErrors: error.data,
          loading: false,
        }));
      }
      toast.error(error.data.message || "It appears that something went wrong");

      if (error.data.recaptchaError) window.grecaptcha.reset();

      return setState((prev) => ({
        ...prev,
        validationErrors: error.data,
        loading: false,
      }));
    }
  };


  useLayoutEffect(() => {
    gsap.fromTo(
      ".signup-title",
      {
        y: -20,
        opacity: 0,
      },
      {
        y: 0,
        opacity: 1,
      }
    );
    gsap.fromTo(
      ".signup-form",
      {
        x: 100,
        opacity: 0,
      },
      {
        x: 0,
        opacity: 1,
      }
    );
    gsap.fromTo(
      ".back-login",
      {
        scale: 0,
        opacity: 0,
      },
      {
        scale: 1,
        opacity: 1,
      }
    );
  }, []);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const isFreePlan = atob(urlParams.get("freeplan")) === "freeForLifePlan";
    if (isFreePlan) {
      let limit = btoa(new Date().toISOString());
      window.location.href = `${window.location.origin}/sign-up?limit=${limit}`;
    }
  }, []);


  const handleNameBlur = async (e) => {
    const f = state.first_name;
    const l = state.last_name;
    if (!!f & !!l) {
      if (!!state.username) return;
      let username = `${f}${l}`.toLowerCase().trim().replace(/\s+/gi, "");
      setState((s) => ({ ...s, username }));

      const isExisting = await checkExistingUsername(username);

      if (isExisting) {
        existingUsernameCache.add(username);
        // if first-last name existing then append a random number
        username += `${generateNDigitID()}`;
        setState((s) => ({ ...s, username }));
      }
    }
  };

  const checkExistingUsername = async (username) => {
    console.log(existingUsernameCache);
    const cacheExists = existingUsernameCache.has(username);

    if (cacheExists) {
      return true;
    }

    const response = await http.post("/verify-username", { username });

    const existsInServer = response.data.exists;

    if (existsInServer === true) {
      existingUsernameCache.add(username);
    }

    return response.data.exists;
  };

  const handleUsernameBlur = async (e) => {
    if (!e.target.value) return;

    const exists = await checkExistingUsername(e.target.value);

    if (exists) {
      setState((s) => ({
        ...s,
        validationErrors: {
          ...s.validationErrors,
          username: "Username already exists",
        },
      }));
    }
  };

  /**
   * call verify email view
   * @param {string} email
   */
  const goToVerifyEmail = (email) => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.set("verify", email);

    history.push({
      pathname: location.pathname,
      search: searchParams.toString(),
    });
  };

  const goToVerifyEmailNew = () => {
    window.location.href = `${BASE_URL}/sign-up?verify=${encodeURIComponent(state.email || "sample@email.com")}`;
  };

  const handleResendEmailVerification = async () => {
    // TODO: resend email verification
    // await new Promise(resolve => setTimeout(resolve, 3000));
    try {

      const response = await http.post('/resend-verification', { email });
      console.log('response: ', response);
      toast.success("The email was sent successfully!");
    } catch (err) {
      console.log(err);
      toast.error(err.data?.message || err.message);
    }
  };

  const signupForm = (
    <div className="w-full">
      <div className="signup-form form-container">
        <h2 className="signup-title form-column-title pb-2">
          {t("createAccount")}
        </h2>
        <form
          className="form-column-container"
          id="create-account-component"
          onSubmit={handleSubmit}
          autoComplete="on"
        >
          <div className="w-full 2md:flex 2md:space-x-5">
            <Input
              inputContainerClass="form-input-container"
              value={state?.first_name}
              autoFocus={true}
              name="first_name"
              autoComplete="given-name"
              outsideLabel={t("firstName")}
              outsideLabelClass="text-sm font-bold text-darkerGray"
              noLabelPaddingY="1rem"
              type="text"
              onChange={handleInputChange}
              errorMessage={state.validationErrors?.first_name}
              onBlur={handleNameBlur}
            />
            <Input
              inputContainerClass="form-input-container"
              value={state?.last_name}
              autoFocus={true}
              name="last_name"
              autoComplete="family-name"
              outsideLabel={t("lastName")}
              outsideLabelClass="text-sm font-bold text-darkerGray"
              noLabelPaddingY="1rem"
              type="text"
              onChange={handleInputChange}
              errorMessage={state.validationErrors?.last_name}
              onBlur={handleNameBlur}
            />
          </div>
          <div className="w-full 2md:flex 2md:space-x-5">
            <Input
              inputContainerClass="form-input-container"
              value={state?.username}
              autoFocus={true}
              name="username"
              autoComplete="username"
              outsideLabel={t("username")}
              outsideLabelClass="text-sm font-bold text-darkerGray"
              noLabelPaddingY="1rem"
              type="text"
              onChange={handleInputChange}
              errorMessage={state.validationErrors?.username}
              onBlur={handleUsernameBlur}
            />
            <Input
              inputContainerClass="form-input-container"
              value={state.email}
              autoFocus={true}
              name="email"
              autoComplete="email"
              outsideLabel={t("emailAddress")}
              outsideLabelClass="text-sm font-bold text-darkerGray"
              noLabelPaddingY="1rem"
              type="email"
              onChange={handleInputChange}
              errorMessage={state.validationErrors?.email}
            />
          </div>
          <div className="w-full 2md:flex 2md:space-x-5">
            <Input
              inputContainerClass="form-input-container"
              showInputValidation={true}
              value={state.password}
              outsideLabel={t("password")}
              outsideLabelClass="text-sm font-bold text-darkerGray"
              noLabelPaddingY="1rem"
              name="password"
              autoComplete="new-password"
              masked={true}
              validationPassed={Object.values(passwordValidation).every(
                (item) => item === true
              )}
              visible={state.showPassword}
              onChangeVisible={() =>
                setState((prev) => ({
                  ...prev,
                  showPassword: !prev.showPassword,
                }))
              }
              type={state.showPassword ? "text" : "password"}
              onChange={handleInputChange}
              errorMessage={state.validationErrors?.password}
            />
            <Input
              inputContainerClass="form-input-container-bottom"
              value={state.password_confirmation}
              outsideLabel={t("confirmPassword")}
              outsideLabelClass="text-sm font-bold text-darkerGray"
              noLabelPaddingY="1rem"
              name="password_confirmation"
              autoComplete="new-password"
              masked={true}
              visible={state.showPasswordConfirm}
              onChangeVisible={() =>
                setState((prev) => ({
                  ...prev,
                  showPasswordConfirm: !prev.showPasswordConfirm,
                }))
              }
              type={state.showPasswordConfirm ? "text" : "password"}
              onChange={handleInputChange}
              errorMessage={state.validationErrors?.password_confirmation}
            />
          </div>
          <PasswordValidation passwordValidation={passwordValidation} />

          <div
            className={`checkbox-container mt-2 ${
              RECAPTCHA_KEY ? "mb-2 " : "mb-3"
            }`}
          >
          <div className="inline-flex items-center space-x-1 whitespace-nowrap">
            <CustomCheckbox
              label={
                <span className="checkbox-text">
                  {t("iAgreeTo")}
                </span>
              }
              name="termsAndConditions"
              checked={state.terms}
              onChange={handleUpdateCheckbox}
            />
            <a
              href="https://milestonespage.com/terms-and-conditions/"
              target="_blank"
              className="text-primary checkbox-text" rel="noreferrer"
            >
              {t("termsConditions")}
            </a>
          </div>

          </div>
          <div
            className={`checkbox-container mt-2 ${
              RECAPTCHA_KEY ? "mb-4 " : "mb-5"
            }`}
          >
            <CustomCheckbox
              label={<p className="checkbox-text">{t("iCertifyOver18")}</p>}
              name={"termsAndConditions"}
              checked={state.over_18_years_old}
              onChange={handleUpdateCheckboxOver18Years}
            />
          </div>

          {RECAPTCHA_KEY && (
            <div className="w-fill mb-5" id="recaptcha-v3">
              <ReCAPTCHA
                sitekey={RECAPTCHA_KEY}
                onChange={(value) => setRecaptcha(value)}
              />

              {recapthcaError && (
                <p className="text-red text-xs mt-1">{recapthcaError}</p>
              )}
            </div>
          )}

          <Button
            buttonName={t("createAccount")}
            disabled={state.loading || !state.terms || !state.over_18_years_old}
            buttonTextClass="text-sm text-white font-semibold"
            buttonRadius="6px"
            buttonClass="relative bg-lightGreen py-3.5 mb-5"
            buttonWidth="100%"
            type="submit"
            icon={
              <div className="absolute right-3 bottom-0 top-0 flex items-center">
                {state.loading && (
                  <LoadingIndicator className="text-white w-5 h-5" />
                )}
              </div>
            }
          />

          {/* footer */}
          <div className="border-t border-gray text-sm font-semibold text-lightBlack py-5">
            <div className="back-login w-full flex justify-center items-center">
              Already have an account? &nbsp; 
              <Link to="/login" className="text-primary">
                {" "} Sign In
              </Link>
            </div>
          </div>
        </form>
      </div>
    </div>
  );

  const verifyEmailView = (
    <div className="px-4 md:px-15 py-6 md:py-[2.625rem] text-center bg-white rounded-md text-neutral-700">
      <div className="flex items-center justify-center mb-6">
        <svg
          width="51"
          height="70"
          viewBox="0 0 51 70"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g clip-path="url(#clip0_4321_14875)">
            <path
              d="M50.6427 9.98825V49.2569C50.4987 49.298 50.5165 49.4214 50.5015 49.5298C49.8157 54.4972 47.2703 58.196 42.8953 60.6248C37.4314 63.6598 31.9538 66.6688 26.4941 69.7106C25.774 70.1111 25.1733 70.0864 24.4574 69.6887C19.058 66.6797 13.6353 63.7092 8.23033 60.7085C3.01193 57.8092 0.392438 53.3506 0.369123 47.3821C0.349923 42.4353 0.365009 37.4885 0.365009 32.5431C0.365009 25.2483 0.365009 17.9536 0.365009 10.6603C0.365009 9.48767 0.672216 9.11738 1.78584 8.85543C2.86929 8.60171 3.95549 8.35348 5.02797 8.05862C7.16882 7.4689 9.03126 6.45129 10.282 4.5244C10.954 3.49033 11.5177 2.4014 11.984 1.26448C12.2062 0.720011 12.5175 0.274291 13.0469 0.00137329H37.9265C38.4546 0.275663 38.7563 0.725497 38.9908 1.26173C39.3364 2.05169 39.7149 2.8293 40.1195 3.59045C41.1906 5.6051 42.7843 7.01358 44.9662 7.74045C46.2609 8.17245 47.5748 8.5304 48.9174 8.76492C49.7046 8.90343 50.3328 9.20378 50.64 9.98825H50.6427ZM47.8779 30.1759C47.8779 24.158 47.871 18.1401 47.8888 12.1208C47.8902 11.564 47.7915 11.3117 47.1661 11.1787C45.2748 10.7796 43.4138 10.2502 41.7296 9.25178C39.3899 7.86388 38.0075 5.6915 36.9117 3.2997C36.7142 2.8677 36.4824 2.72369 36.0051 2.72507C29.1725 2.7429 22.3399 2.75524 15.5073 2.71409C14.6446 2.70861 14.2099 2.91844 13.841 3.73582C12.7438 6.16603 11.2078 8.26022 8.76246 9.52744C7.17568 10.3503 5.47095 10.8358 3.73606 11.2047C3.24507 11.3089 3.08873 11.4859 3.0901 12.0002C3.10656 23.8769 3.07913 35.7536 3.11479 47.6303C3.1285 52.3618 5.26661 55.9166 9.40293 58.2206C14.5651 61.0966 19.7492 63.9341 24.9114 66.8086C25.3461 67.0514 25.6561 67.0459 26.0854 66.8059C31.1488 63.9862 36.2163 61.172 41.3003 58.3907C45.7329 55.966 47.8998 52.2453 47.8833 47.2024C47.8641 41.526 47.8792 35.8496 47.8792 30.1732L47.8779 30.1759Z"
              fill="#333333"
            />
            <path
              d="M10.4708 32.5636C10.4708 27.5702 10.4804 22.5754 10.4571 17.5819C10.4543 17.0388 10.6216 16.77 11.1167 16.563C14.1408 15.2957 16.545 13.2591 18.3923 10.5601C18.6104 10.242 18.8435 10.1172 19.2303 10.1172C23.3995 10.1295 27.5688 10.1295 31.738 10.1172C32.1193 10.1172 32.3606 10.2269 32.5801 10.5505C34.4329 13.2797 36.8837 15.2944 39.9146 16.5876C40.3549 16.7755 40.5181 17.0128 40.5167 17.5024C40.5016 27.4906 40.5071 37.4789 40.5044 47.4658C40.5044 49.4091 39.698 50.8519 37.948 51.8023C33.9255 53.987 29.9291 56.2225 25.9312 58.451C25.609 58.6307 25.3799 58.6184 25.0631 58.4414C20.9816 56.1731 16.892 53.9225 12.805 51.6651C11.1579 50.7559 10.4763 49.298 10.4735 47.4754C10.468 42.5039 10.4722 37.5324 10.4722 32.5623L10.4708 32.5636ZM19.698 34.8869C19.0973 34.928 18.6598 35.2187 18.4568 35.7893C18.2319 36.4215 18.4184 36.9591 18.9464 37.3609C20.4317 38.4896 21.9252 39.6087 23.4174 40.7292C24.5913 41.6097 25.1673 41.5301 26.0355 40.374C28.6015 36.9495 31.1675 33.525 33.7321 30.1005C33.8816 29.9003 34.0407 29.7014 34.1531 29.4806C34.4617 28.8785 34.2862 28.1695 33.754 27.7718C33.2151 27.37 32.5019 27.4097 32.0082 27.8884C31.8463 28.0447 31.7133 28.234 31.5775 28.4164C29.3379 31.402 27.0792 34.3753 24.8766 37.3884C24.4487 37.9726 24.1977 37.9466 23.6917 37.531C22.7426 36.752 21.736 36.0444 20.754 35.3065C20.4413 35.072 20.1176 34.8622 19.698 34.8869Z"
              fill="#0C6ED1"
            />
            <path
              d="M19.6981 34.8869C20.1178 34.8622 20.4415 35.0706 20.7542 35.3065C21.7375 36.0444 22.7442 36.752 23.6918 37.531C24.1979 37.9466 24.4503 37.9726 24.8768 37.3884C27.0793 34.3753 29.3381 31.402 31.5777 28.4164C31.7135 28.2353 31.8465 28.0447 32.0084 27.8884C32.5021 27.4111 33.2153 27.37 33.7542 27.7718C34.2864 28.1681 34.4619 28.8772 34.1533 29.4806C34.0409 29.7014 33.8818 29.9003 33.7323 30.1005C31.1677 33.5264 28.6017 36.9509 26.0357 40.374C25.1689 41.5315 24.5915 41.6111 23.4175 40.7292C21.9254 39.6088 20.4333 38.4897 18.9466 37.361C18.4186 36.9605 18.2321 36.4215 18.457 35.7893C18.66 35.2174 19.0974 34.928 19.6981 34.8869Z"
              fill="white"
            />
          </g>
          <defs>
            <clipPath id="clip0_4321_14875">
              <rect
                width="50.2832"
                height="70"
                fill="white"
                transform="translate(0.359375)"
              />
            </clipPath>
          </defs>
        </svg>
      </div>
      <div className="font-bold text-[1.875rem] tracking-verytight mb-4">
        Verify your Email Address
      </div>
      <p className="mb-4 text-base leading-8">
        We just sent an email confirmation to{" "}
        <span className="font-bold">{state.email}</span> Please click on the link
        found in the email to verify your email address..
      </p>
      <div className="mb-7">
        Didn’t receive the email? <ResendButton onClick={handleResendEmailVerification} />
      </div>

      <NavLink
        to="/login"
        className="bg-neutral-700 text-white w-full p-4 rounded block hover:bg-neutral-500 active:bg-neutral-900 transition-all duration-150"
      >
        Back to Login
      </NavLink>
    </div>
  );

  // Inline Choose Plan View (Promo Lifetime)
  const ChoosePlanInline = () => {
    const [processing, setProcessing] = useState(false);

    const selectFree = async () => {
      // Go straight to verify email view
      setProcessing(true);
      try {
        goToVerifyEmail(state.email);
      } finally {
        setProcessing(false);
      }
    };

    const stripeCheckout = async () => {
      // Use existing public/subscribes endpoint but pass a lifetime plan id
      try {
        setProcessing(true);
        const { http } = global.services;
        const { data } = await http.post("public/signup-subscribes", {
          plan: "Premium Lifetime",
          price_id: STRIPE_AUD_PREMIUM_LIFETIME,
          currency: "aud",
          price_id: STRIPE_AUD_PREMIUM_LIFETIME,
          success_url: `${BASE_URL}/sign-up?verify=${encodeURIComponent(state.email || "")}`,
          cancel_url: `${BASE_URL}/sign-up?chooseplan=1`,
          email: state.email
        });
        window.location.href = data.url;
      } catch (err) {
        console.log(err);
        toast.error(err?.data?.message || err.message);
      } finally {
        setProcessing(false);
      }
    };

    const paypalCheckout = async () => {
      try {
        setProcessing(true);
        const { http } = global.services;
        const { data } = await http.post("public/paypal/signup-subscribes", {
          plan: "Premium Lifetime",
          plan_id: PAYPAL_AUD_PREMIUM_LIFETIME,
          currency: "aud",
          email: state.email,
          success_url: `${BASE_URL}/sign-up?verify=${encodeURIComponent(state.email || "")}`,
          cancel_url: `${BASE_URL}/sign-up?chooseplan=1`,
        });
        window.location.href = data.url;
      } catch (err) {
        console.log(err);
        toast.error(err?.data?.message || err.message);
      } finally {
        setProcessing(false);
      }
    };

    return (
      <div className="w-full">
        <div className="signup-form form-container">
          <h2 className="signup-title form-column-title pb-2">
            Choose a Plan
          </h2>
            {/* Free card */}
            {
              state.showFreePlan && (
                <div onClick={() => setState({ ...state, selectedPlan: "free" })} className={`cursor-pointer border-2 rounded-lg p-4 mb-4 bg-white ${state.selectedPlan!="free"?"border-gray-200":"border-green drop-shadow-xl"}`}>
                  <div className="flex items-center justify-between">
                    <div className="font-semibold text-md"><img className="w-7 h-7 inline mr-2" src={LogoBlueIcon} />Free Account</div>
                    <div className="text-neutral-500 font-bold">FREE</div>
                  </div>
                  <p className="text-sm mt-2 text-neutral-600">With your free account, you can create a Milestones Page complete with a headshot and background image, a bio, searchable milestones, one milestone image, and profile header links to showcase your work and connect with others.</p>
                </div>
              )
            }

            {/* Premium lifetime card */}
            <div onClick={() => setState({ ...state, selectedPlan: "lifetime" })} className={`cursor-pointer w-full border-2 rounded-lg p-0 overflow-hidden bg-white ${state.selectedPlan=="free"?"border-gray-200":"border-green drop-shadow-xl"}`}>
              <div className="text-sm text-neutral-500 bg-[#f6f7f9] px-4 py-2 text-center"><ClockIconSVG className="mr-1 inline w-6 h-5"></ClockIconSVG>Lifetime Promo is valid until 30th September</div>
              <div className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <img className="w-10 h-10" src={FeaturedIcon} />
                    <div className="font-semibold">Premium Account</div>
                  </div>
                  <div className="text-neutral-800 font-bold"><span className="text-2xl">A$67</span><span className="text-base font-normal">/Lifetime</span> <span className="line-through text-neutral-400 text-lg">$249</span></div>
                </div>
                <ul className="mt-3 space-y-2 text-sm text-neutral-700">
                  {[
                    "Headshot & Background image",
                    "Milestones",
                    "Bio",
                    "Search",
                    "Images on Milestones (one image)",
                    "Images/Links on Profile Header",
                    "Advanced Search",
                    "Unlimited milestone buttons (links, PDFs, images, audio, video)",
                    "Messaging (send & receive)",
                    "Analytics: profile & button views",
                    "Bio media: photos & videos",
                    "Verification: blue tick (ID required)",
                    "Hero button",
                    "Mutual connections",
                    "Up to 4 sub-profiles (groups, teams, characters)",
                    "Advanced profile management",
                  ].map((txt, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <CheckCircleIconSVG></CheckCircleIconSVG>
                      <span className="leading-8">{txt}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
                <div className="mt-4 space-y-3 w-full mb-8">
                  {
                    state.selectedPlan != "free" ? (
                      <>
                        <Button
                          buttonName="Paypal Checkout"
                          buttonClass="w-full bg-[#f7be3b] hover:brightness-95 p-4"
                          buttonTextClass="flex items-center justify-center gap-2 text-sm font-semibold text-neutral-800"
                          onClick={paypalCheckout}
                          disabled={processing}
                          icon={processing ? <LoadingIndicator className="w-4 h-4" /> : <PaypalIconSVG className="w-6 h-6" />}
                          iconPosition="left"
                        />
                        <Button
                          buttonName="Use Debit or Credit Card"
                          buttonClass="w-full bg-[#0d6ed1] hover:bg-[#0b58a7] p-4"
                          buttonTextClass="flex items-center justify-center gap-2 text-white text-sm font-semibold"
                          onClick={stripeCheckout}
                          disabled={processing}
                          icon={processing ? <LoadingIndicator className="w-4 h-4" /> : <CardIconSVG />}
                          iconPosition="left"
                        />
                      </>):
                        <Button
                          buttonName="Continue"
                          buttonClass="w-full bg-[#0d6ed1] hover:bg-[#0b58a7] p-4"
                          buttonTextClass="flex items-center justify-center gap-2 text-white text-sm font-semibold"
                          onClick={goToVerifyEmailNew}
                          disabled={processing}
                        />
                  }

                </div>
        </div>
      </div>
    );
  };

  return (
    <Layout title="Create Account">
      <div className="main-page-wrapper relative signup-page-wrapper">
        <div className="login-container relative">
          <div className="2md:max-w-[517px] flex flex-col justify-center">
            <img
              src={LogoAlt}
              alt={"Milestones Page Logo"}
              className="max-w-[301px] w-full"
            />
            <h1 className="text-4xl md:text-5xl font-bold mt-8 mb-6 leading-[1.2]">
              Make your Creative Portfolio impossible to ignore
            </h1>
            <ul className="list-none text-lg border-b border-gray pb-2 2md:pb-9">
              <li className="mb-2 flex items-center gap-4">
                <CheckCircle2IconSVG></CheckCircle2IconSVG>
                <span><b>Control your story</b> and put everything together</span>
              </li>
              <li className="mb-2 flex items-center gap-4">
                <CheckCircle2IconSVG></CheckCircle2IconSVG>
                <span>Add <b>videos, photos,</b> and <b>audio</b> to show what you've done</span>
              </li>
              <li className="mb-2 flex items-center gap-4">
                <CheckCircle2IconSVG></CheckCircle2IconSVG>
                <span>Share theatre <b>programs, reviews,</b> and <b>press links</b></span>
              </li>
              <li className="mb-2 flex items-center gap-4">
                <CheckCircle2IconSVG></CheckCircle2IconSVG>
                <span>Highlight your <b>showreel</b> and top <b>projects</b></span>
              </li>
              <li className="mb-2 flex items-center gap-4">
                <CheckCircle2IconSVG></CheckCircle2IconSVG>
                <span>Tag <b>collaborators</b> and <b>link</b> to your best scenes</span>
              </li>
              <li className="mb-2 flex items-center gap-4">
                <CheckCircle2IconSVG></CheckCircle2IconSVG>
                <span><b>Tailor pages</b> for each audition</span>
              </li>
              <li className="mb-2 flex items-center gap-4">
                <CheckCircle2IconSVG></CheckCircle2IconSVG>
                <span>Keep it current and <b>share one simple link</b></span>
              </li>
            </ul>
            <div className="pt-2 2md:pt-9">
              <p className="text-lg italic text-lightBlack mt-2 mb-3">“Half of who I was as an artist felt invisible online… until I built my Milestones Page.”</p>
              <div className="flex items-center gap-4 mt-2">
                <img src={AvatarHeader} alt="header_image" className="object-cover rounded-full w-[60px] h-[60px]"/> 
                <div>
                  <div className="flex gap-1 text-[#f2994a]">
                    <FaStar/>
                    <FaStar/>
                    <FaStar/>
                    <FaStar/>
                    <FaStar/>
                  </div>
                  <p className="font-bold text-lightBlack text-lg">CJ Fortuna</p>
                </div>
              </div>
            </div>
          </div>
          {{
            "sign-up": signupForm,
            verify: verifyEmailView,
            "choose-plan": <ChoosePlanInline />,
          }[view]}
        </div>
      </div>
    </Layout>
  );
};

function ResendButton({ onClick }) {
  const [loading, setLoading] = useState(false);
  const [sec, setSec] = useState(0);

  const startCountdown = () => {
    setSec(60);
    const interval = setInterval(() => {
      setSec(s => {
        if(s === 0) {
          clearInterval(interval);
          return 0;
        }
        return s - 1;
      })
    }, 1000);
  };

  return (
    <button
      className="inline-flex flex-row items-center gap-1 text-primary font-semibold text-sm"
      disabled={loading || sec > 0}
      style={{ opacity: sec > 0 ? 0.5 : 1}}
      onClick={async () => {
        const click = onClick?.();
        if (click instanceof Promise) {
          setLoading(true);
          try{
            await click;
          } catch(err) {
            throw err;
          } finally {
            setLoading(false);
            startCountdown();
          }
        }
      }}
    >
      {loading && <LoadingIndicator className="w-4 h-4" />}
      {loading ? "Resending Email..." : "Resend Email"}
      {sec > 0 && ` (${sec.toString().padStart(2, '0')})`} 
    </button>
  );
}

const mapStateToProps = (state) => {
  return {
    user: state.user.userData,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    setUserData: (params) => {
      dispatch(userActions.setUserData(params));
    },
  };
};

const useQuery = () => {
  const { search } = useLocation();

  return React.useMemo(() => new URLSearchParams(search), [search]);
};

export default connect(mapStateToProps, mapDispatchToProps)(SignUp);
